import { useEffect, useState } from "react";
// import axios from "axios";
import "./Analytics.scss";
import { fetchGaSummary, fetchGaRealtime } from '../../stores/api_calls/dashboard';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const GoogleAnalytics = () => {
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [realtimeUsers, setRealtimeUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getGaSummary();
    getGaRealtime();

    // 🔁 Auto-refresh realtime users every 30 seconds
    const interval = setInterval(() => {
      getGaRealtime(false); // don't trigger loading spinner
    }, 1800000); // 30 minutes = 30 * 60 * 1000
    // 30000 30s

    return () => clearInterval(interval);
  }, []);

  const getGaSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetchGaSummary();
      if (!res || !res.data) {
        throw new Error("No data received from API");
      }

      const { data: analyticsData } = res;

      // Handle empty data gracefully
      if (!analyticsData.chartData || analyticsData.chartData.length === 0) {
        setError("No analytics data available for this period.");
      }

      setData(analyticsData);
      setChartData(analyticsData.chartData || []);
    } catch (e) {
      console.error("Error fetching GA summary:", e);
      setError(e.message || "Something went wrong while fetching data.");
    } finally {
      setLoading(false);
    }

  }
  
  const getGaRealtime = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const res = await fetchGaRealtime();
      if (res?.data?.realtimeActiveUsers !== undefined) {
        setRealtimeUsers(res.data.realtimeActiveUsers);
      } else {
        setRealtimeUsers(0);
      }

    } catch (e) {
      console.error("Error fetching GA realtime:", e);
      setError(e.message || "Something went wrong while fetching realtime data.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  if (loading) return <div className="analytics-dashboard loading">Loading Google Analytics...</div>;
  
  if (error || !data) {
    return (
      <div className="analytics-dashboard error">
        <p>{error || "No data available"}</p>
        <button onClick={getGaSummary} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <h2>📊 Google Analytics Summary</h2>
      <div className="cards">
        <div className="card">
          <h3>Total Users</h3>
          <p>{data.totalUsers}</p>
        </div>
        <div className="card">
          <h3>Active Users</h3>
          <p>{data.activeUsers}</p>
        </div>
        <div className="card">
          <h3>New Users</h3>
          <p>{data.newUsers}</p>
        </div>
        <div className="card">
          <h3>Sessions</h3>
          <p>{data.sessions}</p>
        </div>

        {/* ⚡ Realtime active users card */}
        <div className="card realtime">
          <h3>Realtime Active Users</h3>
          <p className="realtime-count">{realtimeUsers}</p>
          <small>Auto-updating every 30m</small>
        </div>
      </div>

      <div className="chart-container">
        <h3>Active Users (Last 7 Days)</h3>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="activeUsers"
              stroke="#007aff"
              strokeWidth={3}
              dot={{ r: 4 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GoogleAnalytics;